/* =============================================================
   apiFetch — the single sanctioned path to the /api layer.
   Injects an Entra access token via MSAL, parses JSON, throws a
   typed error on non-2xx so React Query catches naturally.

   Token acquisition: try silent first (uses the cached refresh
   token), then fall back to a redirect login if MSAL needs UX.
   The redirect tears down React state — callers don't need to
   handle it; the post-redirect handleRedirectPromise in lib/msal
   completes the cycle and the page re-mounts.
   ============================================================ */

import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, API_SCOPE, API_BASE_URL } from './msal';

export class ApiError extends Error {
  constructor(public readonly status: number, msg: string) {
    super(msg);
    this.name = 'ApiError';
  }
}

interface ApiInit {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  /** Skip auth — used only for the public invite-lookup endpoint. */
  anonymous?: boolean;
}

async function getAccessToken(): Promise<string> {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new ApiError(401, 'not signed in');
  }
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account,
    });
    return result.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      /* Refresh failed — fall back to redirect. This will reload
         the page; the throw below is mostly defensive. */
      await msalInstance.acquireTokenRedirect({ scopes: [API_SCOPE], account });
      throw new ApiError(401, 'redirecting to sign-in');
    }
    throw e;
  }
}

export async function apiFetch<T = unknown>(path: string, init: ApiInit = {}): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (init.body !== undefined) headers['Content-Type'] = 'application/json';
  if (!init.anonymous) {
    headers['Authorization'] = `Bearer ${await getAccessToken()}`;
  }

  const res = await fetch(url, {
    method: init.method ?? 'GET',
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!res.ok) {
    const message =
      (typeof parsed === 'object' && parsed && 'error' in parsed
        ? String((parsed as { error: unknown }).error)
        : null) ?? `HTTP ${res.status}`;
    /* 410: this sign-in's account was deleted (migration 023); AccountDeletedNotice offers to sign out. */
    if (res.status === 410) window.dispatchEvent(new Event('account-deleted'));
    throw new ApiError(res.status, message);
  }
  return parsed as T;
}
